/**
 * Painel PA da visão clínica: médicos disponíveis, fila e inclusão de paciente.
 * Sem switch, sem editar config, sem iniciar Encounter.
 */
import { memo, useEffect, useState } from "react";
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { PersonAdd } from "@mui/icons-material";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import { ViewHeader } from "../components/ViewHeader";
import { formatBrlInput } from "./moneyUtils";
import { ProntoAtendimentoAttendNowFlow } from "./ProntoAtendimentoAttendNowFlow";
import { ProntoAtendimentoQueueRow } from "./ProntoAtendimentoQueueRow";
import { useProntoAtendimentoClinicDoctors } from "./useProntoAtendimentoClinicDoctors";
import { useProntoAtendimentoQueue } from "./useProntoAtendimentoQueue";
import { PRONTO_ATENDIMENTO_MODE_LABELS } from "./types";
import type { ProntoAtendimentoConfig } from "./types";

interface ScheduleProntoAtendimentoClinicPanelProps {
  isActive: boolean;
  /** Oculta ViewHeader interno na página dedicada /prontoatendimento. */
  hideTitle?: boolean;
}

function doctorChips(
  config: ProntoAtendimentoConfig,
  hasFluxPay: boolean,
): Array<{ label: string; title: string }> {
  const chips: Array<{ label: string; title: string }> = [
    {
      label: PRONTO_ATENDIMENTO_MODE_LABELS[config.mode],
      title: "Modo de atendimento",
    },
  ];
  const specialty = config.specialty || config.healthcareServiceDisplay || "";
  if (specialty) chips.push({ label: specialty, title: "Especialidade / serviço" });
  if (hasFluxPay && config.value > 0) {
    chips.push({
      label: `R$ ${formatBrlInput(config.value)}`,
      title: "Valor do atendimento",
    });
  }
  if (config.locationDisplay) {
    chips.push({ label: config.locationDisplay, title: "Local do atendimento" });
  }
  return chips;
}

function ScheduleProntoAtendimentoClinicPanel({
  isActive,
  hideTitle = false,
}: ScheduleProntoAtendimentoClinicPanelProps) {
  const { hasFluxPayExtension } = useFluxPayContractContext();
  const { doctors, loading: doctorsLoading } = useProntoAtendimentoClinicDoctors({
    enabled: isActive,
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [attendOpen, setAttendOpen] = useState(false);
  /** Config do card clicado em “Incluir paciente” — evita fluxo com médico defasado. */
  const [attendConfig, setAttendConfig] =
    useState<ProntoAtendimentoConfig | null>(null);
  const [queueRefetchFlag, setQueueRefetchFlag] = useState(false);

  const selected =
    doctors.find((d) => d.id === selectedId) || doctors[0] || null;

  useEffect(() => {
    if (!isActive) {
      setAttendOpen(false);
      setAttendConfig(null);
      setSelectedId(null);
    }
  }, [isActive]);

  useEffect(() => {
    if (selected && selected.id !== selectedId) {
      setSelectedId(selected.id);
    }
  }, [selected, selectedId]);

  const {
    rows: queueRows,
    loading: queueLoading,
    refetch: refetchQueue,
  } = useProntoAtendimentoQueue({
    enabled: isActive && Boolean(selected?.practitionerRef),
    professionalActor: selected?.practitionerRef,
  });

  useEffect(() => {
    if (queueRefetchFlag) {
      refetchQueue();
      setQueueRefetchFlag(false);
    }
  }, [queueRefetchFlag, refetchQueue]);

  /** Seleciona o médico do card e abre o fluxo de inclusão com o config correto. */
  const handleIncludePatient = (doctor: ProntoAtendimentoConfig) => {
    setSelectedId(doctor.id);
    setAttendConfig(doctor);
    setAttendOpen(true);
  };

  return (
    <Stack direction="column" width="100%" spacing={2}>
      {!hideTitle && <ViewHeader title="Pronto Atendimento" titleOnly />}

      {doctorsLoading ? (
        <Stack alignItems="center" py={3}>
          <CircularProgress size={28} />
        </Stack>
      ) : doctors.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, borderStyle: "dashed", textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Nenhum médico disponível no horário de atendimento agora.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={600}>
            Médicos disponíveis
          </Typography>
          {doctors.map((doctor) => {
            const isSelected = selected?.id === doctor.id;
            return (
              <Paper
                key={doctor.id}
                variant="outlined"
                sx={{
                  borderColor: isSelected ? "primary.main" : "divider",
                  bgcolor: isSelected ? "primary100.main" : "background.paper",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ px: 1.5, py: 1.25 }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    flexWrap="wrap"
                    useFlexGap
                    onClick={() => setSelectedId(doctor.id)}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700} noWrap>
                      {doctor.practitionerDisplay || "Profissional"}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      {doctorChips(doctor, hasFluxPayExtension).map((chip) => (
                        <Chip
                          key={`${chip.title}-${chip.label}`}
                          size="small"
                          label={chip.label}
                          variant="outlined"
                          sx={{
                            height: 26,
                            bgcolor: "background.paper",
                            borderColor: "divider",
                            maxWidth: 180,
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                  <ButtonBase
                    onClick={() => handleIncludePatient(doctor)}
                    sx={{
                      borderRadius: 2,
                      px: 1,
                      py: 0.75,
                      flexShrink: 0,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                        }}
                      >
                        <PersonAdd fontSize="small" />
                      </Box>
                      <Typography variant="subtitle2" fontWeight={700} noWrap>
                        Incluir paciente
                      </Typography>
                    </Stack>
                  </ButtonBase>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {selected && (
        <Stack spacing={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            Fila · {selected.practitionerDisplay || "Profissional"}
          </Typography>

          {queueLoading ? (
            <Stack alignItems="center" py={3}>
              <CircularProgress size={28} />
            </Stack>
          ) : queueRows.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{ p: 3, borderStyle: "dashed", textAlign: "center" }}
            >
              <Typography variant="body2" color="text.secondary">
                Nenhum paciente na fila. Use Incluir paciente no card do médico.
              </Typography>
            </Paper>
          ) : (
            queueRows.map((row) => (
              <ProntoAtendimentoQueueRow
                key={row.id}
                row={row}
                setRefetch={setQueueRefetchFlag}
                hideStartAction
              />
            ))
          )}
        </Stack>
      )}

      <ProntoAtendimentoAttendNowFlow
        open={attendOpen}
        onClose={() => {
          setAttendOpen(false);
          setAttendConfig(null);
        }}
        config={attendConfig}
        hideStartEncounter
        onSuccess={() => refetchQueue()}
      />
    </Stack>
  );
}

export default memo(ScheduleProntoAtendimentoClinicPanel);
